import { getItem, type ItemSlot } from "@clubap/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { buyItem, fetchWardrobe, type WardrobeState } from "../api.js";
import { AvatarPreview } from "./AvatarPreview.js";
import { SLOTS_IN_ORDER, SLOT_LABELS } from "./slots.js";

interface Props {
  balance: number;
  /** ÍNDICE en `AVATAR_COLORS`: el probador tiñe la figura como el mundo. */
  color: number;
  onBalanceChange: (balance: number) => void;
  onClose: () => void;
}

/**
 * Comprar, nada más. Lo que ya posees se pone desde el inventario.
 *
 * Antes esta lista mezclaba las dos cosas, y el botón cambiaba de significado
 * —comprar o equipar— según si el ítem era tuyo. Separarlo hace que cada panel
 * tenga un solo trabajo.
 *
 * El probador es la mitad izquierda: se prueba antes de pagar. Lo único que se vende
 * acá es aspecto, así que una lista de nombres y precios pedía la decisión sin dar el
 * dato que la sostiene — y el dibujo de cada ítem ya existía, sólo que no llegaba
 * hasta este panel.
 */
export function ShopPanel({ balance, color, onBalanceChange, onClose }: Props) {
  const [wardrobe, setWardrobe] = useState<WardrobeState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** Lo que te estás probando. `null` es "como estás ahora". */
  const [trying, setTrying] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const state = await fetchWardrobe();
      setWardrobe(state);
      onBalanceChange(state.balance);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo abrir la tienda.");
    }
  }, [onBalanceChange]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const buy = async (itemId: string) => {
    setBusy(itemId);
    setError(null);

    try {
      const result = await buyItem(itemId);
      onBalanceChange(result.balance);
      setWardrobe((previous) =>
        previous ? { ...previous, owned: result.owned, balance: result.balance } : previous,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo comprar.");
    } finally {
      setBusy(null);
    }
  };

  const forSale = wardrobe?.items.filter((entry) => !wardrobe.owned.includes(entry.id)) ?? [];

  const bySlot = SLOTS_IN_ORDER.map((slot) => ({
    slot,
    entries: forSale.filter((entry) => getItem(entry.id)?.slot === slot),
  })).filter((group) => group.entries.length > 0);

  const tried = trying ? wardrobe?.items.find((entry) => entry.id === trying) : undefined;

  /*
   * Lo puesto, con lo que se prueba REEMPLAZANDO su ranura.
   *
   * Sumarlo sin más apilaría dos sombreros, que es justo lo que el servidor no
   * permite: una ranura admite un ítem. Verlo mal acá haría que el probador mienta
   * sobre el resultado de la compra.
   */
  const preview = useMemo(() => {
    const equipped = wardrobe?.equipped ?? [];
    if (!tried) return equipped;

    const slot = getItem(tried.id)?.slot;
    return [...equipped.filter((id) => getItem(id)?.slot !== slot), tried.id];
  }, [wardrobe?.equipped, tried]);

  const affordable = tried ? balance >= tried.price : false;

  return (
    /*
     * Sin cierre al tocar el fondo, a propósito. Con botones de compra y de
     * equipar dentro, un clic que se pasa del panel no debería cerrarlo — y era
     * la única diferencia de interacción entre estos paneles y el minijuego.
     * Se cierra con la × o con Escape.
     */
    <div className="overlay">
      <div className="panel sheet sheet--probador">
        <div className="sheet__head">
          <h2 className="sheet__title">Tienda</h2>
          <span className="sheet__coins">◉ {balance}</span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar tienda">
            ×
          </button>
        </div>

        <div className="probador">
          <div className="probador__figura">
            <AvatarPreview worn={preview} color={color} />

            {tried ? (
              <>
                <p className="probador__nombre">{tried.name}</p>
                <p className="probador__ranura">
                  {SLOT_LABELS[getItem(tried.id)?.slot as ItemSlot]}
                </p>
                <p className="probador__precio">◉ {tried.price}</p>

                <button
                  className="btn"
                  type="button"
                  disabled={busy === tried.id || !affordable}
                  title={affordable ? "Comprar" : `Te faltan ${tried.price - balance} monedas`}
                  onClick={() => void buy(tried.id)}
                >
                  {busy === tried.id ? "…" : "Comprar"}
                </button>

                <p className="sheet__hint">
                  {affordable
                    ? `Te quedarían ${balance - tried.price} monedas.`
                    : `Te faltan ${tried.price - balance} monedas.`}
                </p>
              </>
            ) : (
              <p className="sheet__hint">Así estás ahora. Prueba algo de la lista.</p>
            )}
          </div>

          <div className="probador__lista">
            {error && (
              <p className="sheet__error" role="alert">
                {error}
              </p>
            )}

            {!wardrobe && <p className="sheet__hint">Cargando…</p>}

            {wardrobe && forSale.length === 0 && (
              <p className="sheet__empty">Ya tienes todo lo que hay. Vuelve cuando haya más.</p>
            )}

            {bySlot.map((group) => (
              <section className="sheet__group" key={group.slot}>
                <h3 className="sheet__slot">{SLOT_LABELS[group.slot as ItemSlot]}</h3>

                <ul className="sheet__list">
                  {group.entries.map((entry) => {
                    const puede = balance >= entry.price;
                    const probando = trying === entry.id;

                    return (
                      <li
                        className={`sheet__item${probando ? " sheet__item--worn" : ""}`}
                        key={entry.id}
                      >
                        <span className="sheet__name">{entry.name}</span>
                        <span className={`sheet__price${puede ? "" : " sheet__price--short"}`}>
                          ◉ {entry.price}
                        </span>

                        <button
                          className="btn btn--small"
                          type="button"
                          onClick={() => setTrying(probando ? null : entry.id)}
                        >
                          {probando ? "Quitar" : "Probar"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}

            {wardrobe && forSale.length > 0 && (
              <p className="sheet__hint">
                Gana monedas jugando. Lo que compres se pone en el inventario.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
